import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { showPopUp } from '../../../PopUps/actions/PopUpsActions';
import { CREATE_NEW_FOLDER_MODAL } from '../../../PopUps/components/types';
import SiteConstants from '../../../common/SiteConstants/SiteConstants';
import LoadingIndicator from '../../../common/components/LoadingIndicator';
import { getSiteSpecificDefaultImage } from '../../../common/utils';
import FolderActionButton from './FolderActionButton';

import './FolderCard.less';

interface Props {
  name: string;
  uniqueId: string;
  ownerName?: string;
  isOwnedByMe?: boolean;
  thumbnailUrl?: string;
  itemCount?: number;
  updateMemberBinName: (uniqueId: string, newFolderName: string) => void;
  showDeletePopUp: () => void;
  showMakeSharedPopUp: () => void;
  showSharePopUp: () => void;
  cloneMemberBin: (
    uniqueId: string,
    defaultClonedFolderName: string,
    folderAccessControl?: any
  ) => void;
  isUpdating?: boolean;
  isShared?: boolean;
  isInSharedFoldersBeta?: boolean;
  foldersBasePath?: string;
  dispatch: Dispatch;
}

interface State {
  isEditingName: boolean;
  isHovering: boolean;
}

class FolderCard extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isHovering: false,
      isEditingName: false,
    };
  }

  render() {
    const folderContents = this.props.itemCount === 1 ? 'Item' : 'Items';
    return (
      <div
        data-cy="folderCard"
        className="folderCard"
        onMouseEnter={() => this.handleIsHovering()}
        onMouseLeave={() => this.handleIsNotHovering()}
      >
        {this.props.isUpdating && (
          <div className="folderCard-load">
            <LoadingIndicator />
          </div>
        )}

        <a
          href={
            (this.props.foldersBasePath ||
              SiteConstants.buildInstance().getFoldersBasePath()) +
            '/' +
            this.props.uniqueId
          }
          aria-label={`${this.props.name} folder`}
        >
          <div className="folderCard-image">
            <img
              className={this.getCardImageClasses()}
              src={this.getCardImage()}
              loading="eager"
              alt={`${this.props.name} folder thumbnail`}
            />
          </div>
        </a>

        {this.state.isEditingName ? (
          <input
            type="text"
            className="folderCard-name folderCard-nameInput"
            onBlur={(e) => this.saveFolderName(e)}
            onKeyPress={(evt) => {
              evt.key === 'Enter' && this.saveFolderName(evt);
            }}
            defaultValue={this.props.name}
          />
        ) : (
          <h2 className="folderCard-name">{this.props.name}</h2>
        )}
        {this.renderOwnerName()}

        <div className="folderCard-actionRow">
          <span className="folderCard-itemCount">{`${this.props.itemCount} ${folderContents}`}</span>

          <div className="folderCard-actionButtons inline-flex">
            {this.renderDeleteButton()}
            {this.renderShareButton()}
            {this.renderMakePublicButton()}
            <FolderActionButton
              id="copy-folder"
              imageUrl="/assets/common/images/icons/member-bins/file-copy.svg"
              onClickFunction={this.cloneFolder}
              tooltipText="Copy"
            />
            <FolderActionButton
              id="edit-folder-name"
              imageUrl="/assets/common/images/icons/member-bins/edit.svg"
              onClickFunction={this.editFolderName}
              tooltipText="Edit"
            />
          </div>
        </div>
      </div>
    );
  }

  renderDeleteButton() {
    if (!this.props.isOwnedByMe) {
      return null;
    }

    return (
      <FolderActionButton
        id="delete-folder"
        imageUrl="/assets/common/images/icons/member-bins/delete.svg"
        onClickFunction={this.props.showDeletePopUp}
        tooltipText="Delete"
      />
    );
  }

  renderShareButton() {
    return (
      <FolderActionButton
        id="share-folder"
        imageUrl="/assets/common/images/icons/member-bins/share.svg"
        onClickFunction={this.props.showSharePopUp}
        tooltipText="Send Link"
      />
    );
  }

  renderMakePublicButton() {
    if (this.props.isInSharedFoldersBeta && this.isShareablePrivateFolder()) {
      return (
        <FolderActionButton
          id="make-public"
          imageUrl="/assets/common/images/icons/member-bins/shared-bins.svg"
          onClickFunction={this.props.showMakeSharedPopUp}
          tooltipText="Share with Organization"
        />
      );
    }

    return null;
  }

  renderOwnerName = () => {
    const { ownerName, isOwnedByMe } = this.props;

    //For people in Shared Folders Beta, but for whatever reason never set their First and Last name
    let finalOwnerName = 'Unknown';

    //For people not in Shared Folders Beta, ownerName doesn't exist at all and shouldn't be shown
    if (ownerName === undefined) {
      return null;
    } else if (ownerName) {
      if (isOwnedByMe) {
        finalOwnerName = 'Me';
      } else {
        finalOwnerName = ownerName;
      }
    }

    return <div className="folderCard-owner">{finalOwnerName}</div>;
  };

  handleIsHovering = () => {
    this.setState({
      isHovering: true,
    });
  };

  handleIsNotHovering = () => {
    this.setState({
      isHovering: false,
    });
  };

  editFolderName = () => {
    this.setState(
      {
        isEditingName: true,
      },
      function () {
        (
          document.querySelector('.folderCard-nameInput') as HTMLInputElement
        ).focus();
      }
    );
  };

  saveFolderName(e) {
    this.setState({
      isEditingName: false,
    });

    const newFolderName = e.currentTarget.value;
    if (newFolderName !== this.props.name) {
      this.props.updateMemberBinName(this.props.uniqueId, newFolderName);
    }
  }

  cloneFolder = () => {
    const { isInSharedFoldersBeta } = this.props;
    const defaultClonedFolderName = `(Copy) ${this.props.name}`;

    if (isInSharedFoldersBeta) {
      this.props.dispatch(
        showPopUp(CREATE_NEW_FOLDER_MODAL, {
          title: 'Duplicate a folder',
          buttonText: 'Duplicate Folder',
          isInSharedFoldersBeta: isInSharedFoldersBeta,
          inputBinName: defaultClonedFolderName,
          isCloning: true,
          buttonAction: (newFolderName, folderAccessControl) => {
            this.props.cloneMemberBin(
              this.props.uniqueId,
              newFolderName,
              folderAccessControl
            );
          },
        })
      );
    } else {
      this.props.cloneMemberBin(this.props.uniqueId, defaultClonedFolderName);
    }
  };

  getCardImage = () => {
    return this.props.itemCount > 0
      ? this.props.thumbnailUrl
      : getSiteSpecificDefaultImage();
  };

  getCardImageClasses = () => {
    let classes = '';

    if (this.props.isUpdating) {
      classes += ' updating';
    }

    if (this.props.itemCount === 0) {
      classes += ' empty';
    } else {
      classes += ' card-containsItems';
    }

    return classes;
  };

  isShareablePrivateFolder() {
    const { isOwnedByMe, isShared = false } = this.props;

    return isOwnedByMe && !isShared;
  }
}

export default connect()(FolderCard);
